import React, { ReactNode, useEffect, useState, useRef, useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { Box, IconButton, Paper, Badge, Grow, Slide } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactDOM from 'react-dom';

interface GenericBubbleProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  icon: ReactNode;
  badgeContent?: number;
  badgeInvisible?: boolean;
  badgeProps?: {
    anchorOrigin?: {
      vertical: 'top' | 'bottom';
      horizontal: 'left' | 'right';
    };
    sx?: any;
  };
  headerContent: ReactNode;
  mainContent: ReactNode;
  position?: number;
  updateLastActiveContainer?: (id: string) => void;
  containerZIndex?: number;
  containerId: string;
  footerSpacing?: number; // Spacing from the bottom of the viewport
}

// Use extreme z-index values to guarantee stacking order
const BASE_CONTAINER_Z_INDEX = 10000;
const BUBBLE_Z_INDEX = 30000; // Ultra high z-index to ensure buttons are always on top

// Keep track of portal container references across components
let portalRefCount = 0;
let portalContainer: HTMLElement | null = null;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'fixed',
    bottom: '80px',
    right: '16px',
    zIndex: BUBBLE_Z_INDEX,
    pointerEvents: 'none',
  },
  bubbleContainer: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '16px',
    minWidth: '400px',
    '@media (min-width: 600px)': {
      minWidth: '500px',
    },
    '@media (min-width: 960px)': {
      minWidth: '700px',
    },
    '& > *': {
      pointerEvents: 'auto',
    },
  },
  bubbleStack: {
    position: 'fixed',
    right: '16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '16px',
    zIndex: BUBBLE_Z_INDEX, // Always highest z-index
    pointerEvents: 'none',
  },
  contentWrapper: {
    position: 'fixed',
    right: 0,
    top: 0,
    bottom: '0',
    width: 'min(90vw, 600px)',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : '#212121',
    borderRadius: '8px',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.45)',
    overflow: 'hidden',
    transition: 'transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.3s ease',
    zIndex: BUBBLE_Z_INDEX + 1000,
  },
  headerArea: {
    padding: '16px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : '#212121',
    zIndex: 2,
    position: 'sticky',
    top: 0,
  },
  mainContent: {
    flex: 1,
    padding: '16px',
    paddingRight: '88px', // Ensure content doesn't overlap with bubble buttons
    overflowY: 'auto',
    height: 'calc(100vh)',
    backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : '#2d2d2d !important',
    position: 'relative',
    zIndex: 5,
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: theme.palette.mode === 'light' ? '#f1f1f1' : '#333333',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.mode === 'light' ? '#cccccc' : '#666',
      borderRadius: '4px',
      '&:hover': {
        background: theme.palette.mode === 'light' ? '#aaaaaa' : '#888',
      },
    },
  },
  bubbleButton: {
    position: 'relative',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.secondary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? '#444444' : theme.palette.grey[300],
      transform: 'scale(1.1)',
      border: `2px solid ${theme.palette.secondary.main}`,
    },
    '&.open': {
      backgroundColor: theme.palette.secondary.main,
      '&:hover': {
        backgroundColor: theme.palette.secondary.dark,
        border: `2px solid ${theme.palette.secondary.light}`,
      },
    },
    width: 56,
    height: 56,
    borderRadius: '50%',
    boxShadow:
      '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)',
    '& .MuiSvgIcon-root': {
      width: 24,
      height: 24,
    },
    zIndex: BUBBLE_Z_INDEX,
    transition:
      'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease, border 0.2s ease',
    border: '2px solid transparent',
    '&:active': {
      transform: 'scale(0.95)',
    },
  },
  badge: {
    '& .MuiBadge-badge': {
      right: 4,
      top: 4,
      border: `2px solid ${theme.palette.background.paper}`,
      padding: '0 4px',
    },
  },
  collapseContainer: {
    position: 'fixed',
    right: 0,
    top: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'flex-end',
    pointerEvents: 'none',
  },
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    pointerEvents: 'none',
    zIndex: BASE_CONTAINER_Z_INDEX - 500,
  },
  backdropVisible: {
    opacity: 0,
    pointerEvents: 'none',
  },
  // New portal container for bubble buttons
  bubblePortalContainer: {
    position: 'fixed',
    inset: 0,
    pointerEvents: 'none',
    zIndex: BUBBLE_Z_INDEX,
  },
}));

// Create a portal container for the bubble buttons if it doesn't exist
const getPortalContainer = () => {
  if (!portalContainer) {
    portalContainer = document.getElementById('bubble-buttons-portal');
    if (!portalContainer) {
      portalContainer = document.createElement('div');
      portalContainer.id = 'bubble-buttons-portal';
      portalContainer.style.position = 'fixed';
      portalContainer.style.inset = '0';
      portalContainer.style.pointerEvents = 'none';
      portalContainer.style.zIndex = BUBBLE_Z_INDEX.toString();
      document.body.appendChild(portalContainer);
    }
  }
  portalRefCount++;
  return portalContainer;
};

// Safely remove portal container when no longer needed
const removePortalContainer = () => {
  portalRefCount--;

  // Use setTimeout to ensure all components have completed their cleanup
  // before potentially removing the shared DOM element
  setTimeout(() => {
    if (portalRefCount <= 0 && portalContainer && document.body.contains(portalContainer)) {
      try {
        document.body.removeChild(portalContainer);
        portalContainer = null;
        portalRefCount = 0;
      } catch (e) {
        console.warn('Failed to remove portal container, it may have already been removed');
      }
    }
  }, 0);
};

export default function GenericBubble({
  isOpen,
  setIsOpen,
  icon,
  badgeContent = 0,
  badgeInvisible = false,
  badgeProps,
  headerContent,
  mainContent,
  position = 0,
  updateLastActiveContainer,
  containerZIndex = BASE_CONTAINER_Z_INDEX,
  containerId,
}: GenericBubbleProps) {
  const classes = useStyles();
  const theme = useTheme();

  // Add ref to track the bubble content element
  const bubbleContentRef = useRef<HTMLDivElement>(null);
  const bubbleButtonRef = useRef<HTMLDivElement>(null);

  // Default position (at the bottom)
  const defaultPosition = 16 + position * 72;

  // States
  const [bottomPosition, setBottomPosition] = useState(defaultPosition);
  const [isVisible, setIsVisible] = useState(false);
  const [isSiteLoaded, setIsSiteLoaded] = useState(false);
  const footerObserverRef = useRef<IntersectionObserver | null>(null);
  const lastUrlRef = useRef(window.location.href);
  const [buttonPortalContainer, setButtonPortalContainer] = useState<HTMLElement | null>(null);

  // Add click outside listener to close the bubble when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      // Get the clicked element
      const target = event.target as Element;

      // Check if the click was inside a MUI Popper, Popover, Select dropdown, or any modal
      const isInPopper = !!target.closest('.MuiPopper-root');
      const isInPopover = !!target.closest('.MuiPopover-root');
      const isInMenu = !!target.closest('.MuiMenu-root');
      const isInModal = !!target.closest('.MuiModal-root');
      const isInDatePicker = !!target.closest('.MuiPickersPopper-root');

      // If click was in any dropdown or modal component, don't close the bubble
      if (isInPopper || isInPopover || isInMenu || isInModal || isInDatePicker) {
        return;
      }

      // Check if click was outside bubble content and bubble button
      if (
        bubbleContentRef.current &&
        !bubbleContentRef.current.contains(target as Node) &&
        bubbleButtonRef.current &&
        !bubbleButtonRef.current.contains(target as Node)
      ) {
        setIsOpen(false);
      }
    };

    // Add click listener
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  // Register and use portal container
  useEffect(() => {
    const container = getPortalContainer();
    setButtonPortalContainer(container);

    return () => {
      // Safely decrement ref count and maybe remove container
      removePortalContainer();
    };
  }, []);

  // Safety buffer
  const safetyBuffer = 16;

  // Calculate position when footer is visible - memoized with useCallback
  const updatePositionForFooter = useCallback(() => {
    // Find the footer
    const footer =
      document.querySelector('footer') ||
      document.getElementById('footer') ||
      document.querySelector('.footer');

    if (!footer) {
      // No footer, use default position at bottom
      setBottomPosition(defaultPosition);
      return;
    }

    // Get footer position info
    const footerRect = footer.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // If footer is not visible in viewport, use default position
    if (footerRect.top >= viewportHeight) {
      setBottomPosition(defaultPosition);
      return;
    }

    // Calculate how much of the footer is visible
    const footerVisibleAmount = Math.min(footerRect.height, viewportHeight - footerRect.top);

    // Set position to stay above the visible part of the footer
    setBottomPosition(footerVisibleAmount + safetyBuffer + position * 72);
  }, [defaultPosition, position, safetyBuffer]);

  // Detect when document is fully loaded
  useEffect(() => {
    // Initialize as invisible during load
    setIsVisible(false);

    // Function to run when the window and all resources are fully loaded
    const handleCompleteLoad = () => {
      setTimeout(() => {
        // Set as site loaded
        setIsSiteLoaded(true);

        // Position at default first
        setBottomPosition(defaultPosition);

        // Then check footer
        updatePositionForFooter();

        // Finally make visible with another delay to ensure smooth appearance
        setTimeout(() => {
          setIsVisible(true);
        }, 200);
      }, 1000); // Wait 1s after load event to ensure stability
    };

    // Check if already loaded
    if (document.readyState === 'complete') {
      handleCompleteLoad();
    } else {
      // Listen for load event
      window.addEventListener('load', handleCompleteLoad);

      // Fallback if load event doesn't fire
      const timeoutId = setTimeout(handleCompleteLoad, 3000);

      return () => {
        window.removeEventListener('load', handleCompleteLoad);
        clearTimeout(timeoutId);
      };
    }
  }, [defaultPosition, updatePositionForFooter]);

  // Set up scroll listener to detect when footer becomes visible
  useEffect(() => {
    // Only add scroll listeners when site is loaded and bubbles are visible
    if (!isSiteLoaded) return;

    // Throttle function to avoid too frequent updates
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const handleScroll = () => {
      if (timeout) return;

      timeout = setTimeout(() => {
        updatePositionForFooter();
        timeout = null;
      }, 100);
    };

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Initial check
    updatePositionForFooter();

    return () => {
      if (timeout) clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isSiteLoaded, updatePositionForFooter]);

  // Reset on route change
  useEffect(() => {
    const handleRouteChange = () => {
      if (window.location.href !== lastUrlRef.current) {
        // Store new URL
        lastUrlRef.current = window.location.href;

        // Hide immediately
        setIsVisible(false);
        // Reset site loaded flag
        setIsSiteLoaded(false);

        // Use same loading logic as initial load
        setTimeout(() => {
          // Set as site loaded
          setIsSiteLoaded(true);

          // Position at default first
          setBottomPosition(defaultPosition);

          // Then check footer
          updatePositionForFooter();

          // Finally make visible with delay
          setTimeout(() => {
            setIsVisible(true);
          }, 200);
        }, 1000);
      }
    };

    // Listen for route changes
    const observer = new MutationObserver(() => {
      handleRouteChange();
    });

    observer.observe(document, { subtree: true, childList: true });

    // Handle back/forward navigation
    window.addEventListener('popstate', () => {
      // Hide immediately
      setIsVisible(false);
      // Reset site loaded flag
      setIsSiteLoaded(false);

      // Use same loading logic
      setTimeout(() => {
        setIsSiteLoaded(true);
        setBottomPosition(defaultPosition);
        updatePositionForFooter();

        setTimeout(() => {
          setIsVisible(true);
        }, 200);
      }, 1000);
    });

    return () => {
      observer.disconnect();
      window.removeEventListener('popstate', updatePositionForFooter);
    };
  }, [defaultPosition, updatePositionForFooter]);

  // Set up footer observer to detect when footer enters/exits viewport
  useEffect(() => {
    // Only observe footer when site is loaded
    if (!isSiteLoaded) return;

    const footer =
      document.querySelector('footer') ||
      document.getElementById('footer') ||
      document.querySelector('.footer');

    if (!footer) return;

    const options = {
      threshold: [0, 0.25, 0.5, 0.75, 1.0],
    };

    footerObserverRef.current = new IntersectionObserver((entries) => {
      const entry = entries[0];

      if (entry.isIntersecting) {
        // Footer is visible, update position
        updatePositionForFooter();
      } else {
        // Footer not visible, reset to bottom
        setBottomPosition(defaultPosition);
      }
    }, options);

    footerObserverRef.current.observe(footer);

    return () => {
      if (footerObserverRef.current) {
        footerObserverRef.current.disconnect();
      }
    };
  }, [isSiteLoaded, defaultPosition, updatePositionForFooter]);

  const handleToggle = () => {
    // Update last active container when clicked
    if (updateLastActiveContainer && containerId) {
      updateLastActiveContainer(containerId);
    }
    setIsOpen(!isOpen);
  };

  // Handle clicks on bubble content to update active container
  const handleContentClick = () => {
    if (updateLastActiveContainer && containerId) {
      updateLastActiveContainer(containerId);
    }
  };

  // Render bubble buttons through portal to ensure they're always on top
  const renderBubbleButtons = () => {
    if (!isVisible) return null;

    return (
      <Box
        ref={bubbleButtonRef}
        className={classes.bubbleStack}
        sx={{
          bottom: `${bottomPosition}px`,
          opacity: isVisible ? 1 : 0,
          visibility: isVisible ? 'visible' : 'hidden',
          transition: isVisible ? 'opacity 0.3s ease' : 'none',
          display: isVisible ? 'flex' : 'none',
          zIndex: BUBBLE_Z_INDEX, // Always use the highest z-index
        }}
      >
        <Badge
          badgeContent={badgeContent}
          color="primary"
          className={classes.badge}
          max={99}
          invisible={badgeInvisible || badgeContent <= 0}
          {...(badgeProps || {})}
          sx={{
            pointerEvents: 'auto',
            position: 'relative',
            zIndex: BUBBLE_Z_INDEX, // Ensure badge has highest z-index
            ...(badgeProps?.sx || {}),
          }}
        >
          <IconButton
            className={`${classes.bubbleButton} ${isOpen ? 'open' : ''}`}
            onClick={handleToggle}
            aria-label={isOpen ? 'close' : 'open'}
            size="large"
            sx={{
              transform: isOpen ? 'rotate(360deg)' : 'rotate(0deg)',
              transition:
                'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.3s ease',
              position: 'relative',
              zIndex: BUBBLE_Z_INDEX, // Always use the highest z-index
              pointerEvents: 'auto',
            }}
          >
            {icon}
          </IconButton>
        </Badge>
      </Box>
    );
  };

  // Create a useEffect inside the component to add a global style for poppers
  useEffect(() => {
    // Add global style for all poppers to ensure they're on top
    const style = document.createElement('style');
    style.innerHTML = `
      .MuiPopper-root, .MuiAutocomplete-popper, .MuiPopover-root, .MuiMenu-root {
        z-index: ${BUBBLE_Z_INDEX + 2000} !important;
      }
      
      /* Fix for scrollbar disappearing with dropdowns */
      .MuiPopover-root, .MuiMenu-root {
        overflow: visible !important;
      }
      
      .MuiModal-root + .MuiBackdrop-root {
        z-index: auto !important;
      }
      
      /* Ensure content remains scrollable with open dropdowns */
      .MuiPopover-paper, .MuiMenu-paper {
        max-height: 300px;
        overflow-y: auto !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <>
      {/* Backdrop element is invisible */}
      <Box
        className={`${classes.backdrop} ${isOpen ? classes.backdropVisible : ''}`}
        sx={{ zIndex: BASE_CONTAINER_Z_INDEX - 500, display: 'none' }}
      />

      {/* Container with the opened bubble content */}
      <Box
        className={classes.collapseContainer}
        sx={{
          zIndex: containerZIndex, // Use the dynamic container z-index
          display: isVisible ? 'flex' : 'none',
        }}
      >
        <Slide
          direction="left"
          in={isOpen}
          mountOnEnter
          unmountOnExit
          timeout={{
            enter: 400,
            exit: 300,
          }}
        >
          <Grow
            in={isOpen}
            style={{
              transformOrigin: 'right center',
            }}
            timeout={{
              enter: 400,
              exit: 300,
            }}
          >
            <Paper
              ref={bubbleContentRef}
              className={classes.contentWrapper}
              elevation={16}
              sx={{
                borderLeft: `2px solid ${theme.palette.secondary.main}`,
                pointerEvents: 'auto',
                zIndex: containerZIndex, // Use the dynamic container z-index
              }}
              onClick={handleContentClick}
            >
              <Box className={classes.headerArea}>{headerContent}</Box>
              <Box className={classes.mainContent}>{mainContent}</Box>
            </Paper>
          </Grow>
        </Slide>
      </Box>

      {/* Render bubble buttons through portal to the highest level of DOM */}
      {buttonPortalContainer && ReactDOM.createPortal(renderBubbleButtons(), buttonPortalContainer)}
    </>
  );
}
